import { create } from "zustand";

import type { FolderItemOut, NoteCategory } from "@/lib/api";

/**
 * ONE selection model, shared by every surface that shows files: the left rail's
 * explorer, Folder View, and (later) the homepage grid.
 *
 * Structural point, not a convenience: before this, the left rail owned a single
 * `selectedId` string and the homepage owned nothing, so "select three notes and
 * move them" was expressible on no surface at all, and a file dragged in one
 * place meant nothing in another. Multi-select, the clipboard and drag payloads
 * all live here so the three surfaces cannot drift.
 *
 * The anchor is what makes Shift-click mean what it means everywhere else: a
 * range runs from the last plainly-clicked row to the shift-clicked one, and is
 * REPLACED (not accumulated) as the shift-click moves, so shift-clicking around
 * doesn't leave a trail of half-selected rows behind it.
 */

export interface FileRef {
  kind: "document" | "node";
  id: string;
  title: string;
  category: NoteCategory;
  /** The folder it currently sits in ("" = the category root). */
  folder: string;
  /** The graph node behind it, when there is one — what the inspector is
   * keyed by, so selecting a file here can populate the right-hand panel. */
  nodeId?: string | null;
}

export const refKey = (ref: { kind: string; id: string }): string => `${ref.kind}:${ref.id}`;

export function toFileRef(item: FolderItemOut, folder: string): FileRef {
  return {
    kind: item.kind,
    id: item.id,
    title: item.title,
    category: item.category,
    folder,
    nodeId: item.node_id ?? (item.kind === "node" ? item.id : null),
  };
}

/** What a cut/copy is holding, and which it was. */
export interface Clipboard {
  mode: "cut" | "copy";
  refs: FileRef[];
}

interface SelectionState {
  /** `kind:id` → the ref, so a selection survives the tree re-ordering under it. */
  selected: Map<string, FileRef>;
  /** The row a Shift-range measures from. */
  anchorKey: string | null;
  /** In-app clipboard. The OS clipboard cannot carry a vault entry, so Ctrl+X /
   * Ctrl+C keep their meaning here without pretending to be system-wide. */
  clipboard: Clipboard | null;

  isSelected: (ref: { kind: string; id: string }) => boolean;
  selectedRefs: () => FileRef[];
  count: () => number;

  /** Plain click: this row becomes the whole selection, and the anchor. */
  select: (ref: FileRef) => void;
  /** Ctrl/Cmd-click: add or remove one row, moving the anchor to it. */
  toggle: (ref: FileRef) => void;
  /** Shift-click: replace the range from the anchor with `range`. */
  selectRange: (range: FileRef[]) => void;
  /** Marquee / Ctrl+A: set the selection outright. */
  setSelection: (refs: FileRef[], anchor?: FileRef | null) => void;
  clear: () => void;

  cut: (refs?: FileRef[]) => void;
  copy: (refs?: FileRef[]) => void;
  clearClipboard: () => void;
}

export const useFileSelection = create<SelectionState>()((set, get) => ({
  selected: new Map(),
  anchorKey: null,
  clipboard: null,

  isSelected: (ref) => get().selected.has(refKey(ref)),
  selectedRefs: () => [...get().selected.values()],
  count: () => get().selected.size,

  select: (ref) => set({ selected: new Map([[refKey(ref), ref]]), anchorKey: refKey(ref) }),

  toggle: (ref) =>
    set((s) => {
      const selected = new Map(s.selected);
      const key = refKey(ref);
      if (selected.has(key)) selected.delete(key);
      else selected.set(key, ref);
      return { selected, anchorKey: key };
    }),

  selectRange: (range) =>
    // Replaces rather than accumulates: shift-clicking a different row must
    // re-measure the range from the same anchor, not union with the last one.
    set({ selected: new Map(range.map((r) => [refKey(r), r])) }),

  setSelection: (refs, anchor) =>
    set({
      selected: new Map(refs.map((r) => [refKey(r), r])),
      anchorKey: anchor === undefined ? (refs.at(-1) ? refKey(refs.at(-1)!) : null) : anchor ? refKey(anchor) : null,
    }),

  clear: () => set({ selected: new Map(), anchorKey: null }),

  cut: (refs) => set({ clipboard: { mode: "cut", refs: refs ?? get().selectedRefs() } }),
  copy: (refs) => set({ clipboard: { mode: "copy", refs: refs ?? get().selectedRefs() } }),
  clearClipboard: () => set({ clipboard: null }),
}));

/**
 * Resolve a click into the new selection, given the row clicked, the ordered
 * rows it sits among, and the modifier keys.
 *
 * Kept as a pure function so the rule — plain replaces, Ctrl toggles, Shift
 * ranges from the anchor — is one implementation with one test, rather than
 * being re-derived (slightly differently) by each surface's click handler.
 */
export function applyClick(
  ref: FileRef,
  ordered: FileRef[],
  modifiers: { ctrl?: boolean; shift?: boolean },
): void {
  const store = useFileSelection.getState();
  if (modifiers.shift) {
    const anchorKey = store.anchorKey;
    const from = ordered.findIndex((r) => refKey(r) === anchorKey);
    const to = ordered.findIndex((r) => refKey(r) === refKey(ref));
    if (from >= 0 && to >= 0) {
      const [lo, hi] = from <= to ? [from, to] : [to, from];
      store.selectRange(ordered.slice(lo, hi + 1));
      return;
    }
    // No usable anchor (the anchored row was filtered away, or this is the
    // first click) — a shift-click then behaves as a plain one rather than
    // selecting nothing, which would read as the click being swallowed.
    store.select(ref);
    return;
  }
  if (modifiers.ctrl) {
    store.toggle(ref);
    return;
  }
  store.select(ref);
}

/**
 * The refs a gesture should act on: the whole selection when the row that was
 * grabbed is part of it, otherwise just that row.
 *
 * This is the rule every file manager follows and nobody notices until it is
 * wrong — dragging an unselected file must not carry an unrelated selection
 * along with it, and dragging one of five selected files must carry all five.
 */
export function refsForGesture(ref: FileRef): FileRef[] {
  const store = useFileSelection.getState();
  return store.isSelected(ref) ? store.selectedRefs() : [ref];
}
